import { useAuth } from '@agentnet/auth';
import { CellStatus, DataTable, pxToRem, Status } from '@fluentsms/agentnet-web-components';
import { Card, Checkbox, Grid, IconButton, Paper, Popper, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import {
  DeleteUserAccessKey,
  getUserAcessKeys,
  revokeUserAccessKey,
  UnmaskUserAccessKey,
} from 'api/admin/user-profile-api';
import clsx from 'clsx';
import useAsync from 'hooks/useAsync';
import moment from 'moment';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import CloseIcon from 'ui-kit/icons/Close';
import { AgentNetTextInput } from 'ui-kit/inputs';
import { UserAccessKeyStatus } from './constants';

export interface AccessKeyOptionType {
  ThirdPartyVendorId?: string | number;
  vendorName: string;
  Status?: string | undefined;
  accessKey?: string | undefined;
  visible?: boolean | undefined;
  lastUpdated?: string | undefined;
}

export interface UserAccessKeyType {
  [key: string]: unknown;
  ThirdPartyVendor: any;
  UserAccessKeyId?: string;
  UserAccessKey: string;
  ThirdPartyVendorId: string;
  bmaskFlag?: boolean;
  Status?: UserAccessKeyStatus;
  LastUpdatedDate?: string;
}

interface UserAccessKeyComponentProps {
  accessKeyOptions: AccessKeyOptionType[];
  onChange: (value: any) => void;
  value?: AccessKeyOptionType[];
}

export interface UserAccessKeyComponentRef {
  togglePopper: (anchor: HTMLElement | null) => void;
}

const popperWidth = 400;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const accessKeyStatusColors = {
  Created: '#2E7D32',
  Activated: '#2E7D32',
  Revoked: '#D32F2F',
};

const getAccessKeyStatusLabel = (status?: UserAccessKeyStatus): string =>
  status === UserAccessKeyStatus.Created
    ? 'Created'
    : status === UserAccessKeyStatus.Activated
    ? 'Activated'
    : 'Revoked';

interface AccessKeyCellProps {
  accessKey: UserAccessKeyType;
  rowIndex: number;
  classes: Record<'tableCell' | 'accessKeyText' | 'Icon' | 'copyIcon', string>;
  onUnmask: (id?: string) => Promise<string>;
  onCopy: (id?: string) => void;
}

const AccessKeyCell = ({ accessKey, rowIndex, classes, onUnmask, onCopy }: AccessKeyCellProps) => {
  const [unmaskedValue, setUnmaskedValue] = useState('');
  const isLoadingRef = React.useRef(false);
  const isRevoked = accessKey.Status === UserAccessKeyStatus.Revoked;

  const handleMouseEnter = async () => {
    if (isRevoked || unmaskedValue || isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      setUnmaskedValue(await onUnmask(accessKey.UserAccessKeyId));
    } finally {
      isLoadingRef.current = false;
    }
  };

  return (
    <div className={classes.tableCell}>
      <Tooltip title={isRevoked ? '' : unmaskedValue || 'Loading User Access Key'}>
        <Typography variant="body2" className={classes.accessKeyText} onMouseEnter={handleMouseEnter}>
          {accessKey.UserAccessKey}
        </Typography>
      </Tooltip>
      {!isRevoked && (
        <CopyIcon
          className={clsx(classes.Icon, classes.copyIcon)}
          data-testid={`UakCopy${rowIndex}`}
          onClick={() => onCopy(accessKey.UserAccessKeyId)}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      width: '100%',
      padding: '0 0 24px',
      '& .ag-pinned-right-header, & .ag-pinned-right-cols-container': {
        borderLeft: `1px solid ${theme.palette.divider}`,
      },
    },
    accessKeyText: {
      fontFamily: 'monospace',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statusCell: {
      display: 'flex !important',
      alignItems: 'center !important',
      whiteSpace: 'nowrap',
      '& > *': {
        display: 'flex !important',
        flexDirection: 'row !important',
        alignItems: 'center !important',
        flexWrap: 'nowrap !important',
      },
      '& > * > *': {
        flexShrink: 0,
      },
    },
    tableHeader: {
      backgroundColor: '#F2F5F6',
      borderBottom: '1px solid #D9E0E5',
      fontWeight: 600,
      fontSize: pxToRem(14),
    },
    tableHeaderCell: {
      padding: pxToRem(12),
      display: 'flex',
    },
    tableCellBorderLeft: {
      borderLeft: `1px solid #E0E0E0`,
      maxWidth: 90,
      margin: '0 0 0 auto',
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
    },
    tableRow: {
      borderBottom: `1px solid #E0E0E0`,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: theme.spacing(1),
      flexShrink: 0,
    },
    statusDotActive: {
      backgroundColor: theme.palette.success.main,
    },
    statusDotRevoked: {
      backgroundColor: theme.palette.error.main,
    },
    Icon: {
      '&:hover': {
        cursor: 'pointer',
      },
      padding: pxToRem(2, 0, 2, 3),
    },
    copyIcon: {
      marginLeft: 'auto',
      color: theme.palette.primary.main,
      flexShrink: 0,
    },
    toolTip: {
      '&:hover': {
        cursor: 'default',
      },
    },
    tableCell: {
      padding: pxToRem(12),
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    actionsCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    tableCellHidden: {
      overflow: 'hidden',
    },
    deleteRow: {
      padding: 0,
      marginLeft: theme.spacing(1),
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    my1: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    mb1: {
      marginBottom: theme.spacing(1),
    },
    checkbox: {
      borderRadius: '4px',
      width: '10px',
      marginRight: theme.spacing(1),
      transition: '.2s all',
    },
    deleteCell: {
      justifyContent: 'center',
    },
    ctaBtn: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(3),
      width: '100%',
    },
    validationMsg: {
      width: '100%',
      fontSize: pxToRem(14),
    },
    popper: {
      backgroundColor: '#FFF',
      border: '1px solid #c4c4c4',
      borderRadius: '6px',
      zIndex: 2000,
    },
    popperWrap: {
      width: popperWidth,
      backgroundColor: '#fff',
    },
    textInputWrap: {
      borderBottom: '1px solid #c4c4c4',
      padding: theme.spacing(2),
    },
    textInput: {
      '&>div': {
        paddingRight: '8px !important',
      },
    },
    autocompletePopper: {
      height: 0,
    },
    autocompleteOptionsPaper: {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      width: popperWidth,
    },

    popperContent: {
      width: 400,
      boxShadow:
        '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
    },
    popperHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #c4c4c4',
      padding: '0 8px 0 24px',
    },
    popperClose: {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    popperOption: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: '8px 16px',
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      '&[data-focus="true"]': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    popperDisablePortal: {
      position: 'relative',
      width: '100% !important',
    },
    popperPaper: {
      margin: 0,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  }),
);

const CustomPopper = (props: any) => {
  const classes = useStyles();
  //force the list to only open downwards
  return (
    <Popper
      className={classes.autocompletePopper}
      {...props}
      modifiers={{
        filp: { enabled: false },
        preventOverflow: {
          enabled: 'auto',
          boundariesElement: 'scrollParent',
        },
      }}
      popperOptions={{ placement: 'bottom-start' }}
      placement="bottom-start"
      disablePortal
    />
  );
};
const CustomPaper = ({ children }: any) => {
  const classes = useStyles();
  return <Card className={classes.autocompleteOptionsPaper}>{children}</Card>;
};
const UserAccessKeyComponent = forwardRef<UserAccessKeyComponentRef, UserAccessKeyComponentProps>(
  ({ accessKeyOptions, onChange, value }, ref) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [selectedKeys, setSelectedKeys] = useState<AccessKeyOptionType[]>(value ?? []);
    const [userAccessKeyData, setuserAccessKeyData] = useState<UserAccessKeyType[]>();
    const { getAccessToken } = useAuth();
    const { addSnackbarMessage } = useSnackBars();
    const userAccessKeysData = async () => {
      const token = await getAccessToken();
      const userAccessresponse = await getUserAcessKeys(token);
      setuserAccessKeyData(userAccessresponse);
    };

    const { execute: executeUserAccessKeysData } = useAsync(userAccessKeysData, false);

    useEffect(() => {
      executeUserAccessKeysData().then();
    }, []);

    const togglePopper = (anchor: HTMLElement | null) => {
      setAnchorEl((prev: any) => (prev ? null : anchor));
    };

    useImperativeHandle(ref, () => ({
      togglePopper,
    }));

    const open = Boolean(anchorEl);
    const id = open ? 'autocomplete-checklist-popper' : undefined;

    const handleChange = async (val: AccessKeyOptionType[]) => {
      onChange && (await onChange(val));
      await setSelectedKeys(val);
      await executeUserAccessKeysData().then();
    };

    useEffect(() => {
      const selectedUserAccessKeyData = userAccessKeyData?.filter((x) => x.Status !== UserAccessKeyStatus.Revoked);
      const selectedUserAccessKey: any = selectedUserAccessKeyData?.map((x) => {
        return {
          vendorName: x.ThirdPartyVendor.VendorName,
          ThirdPartyVendorId: x.ThirdPartyVendor.ThirdPartyVendorId,
        };
      });
      setSelectedKeys(selectedUserAccessKey || []);
    }, [userAccessKeyData]);

    const [menuOpen, setMenuOpen] = useState(true);

    const copyUserAccessKey = async (useraccesskeyId: any) => {
      const token = await getAccessToken();
      const finalResponse = await UnmaskUserAccessKey(token, useraccesskeyId);
      navigator.clipboard
        .writeText(finalResponse.UserAccessKey)
        .then(() => {
          addSnackbarMessage({
            message: 'Text Copied',
            type: 'success',
          });
        })
        .catch(() => {
          addSnackbarMessage({
            message: 'Text Could Not Be Copied',
            type: 'failure',
          });
        });
    };

    const unmaskKeyData = async (e: any) => {
      const token = await getAccessToken();
      const unmaskKeyresponse = await UnmaskUserAccessKey(token, e);
      return unmaskKeyresponse.UserAccessKey;
    };

    const handleAccessKeyDelete = async (key: any, action: any) => {
      const token = await getAccessToken();
      if (action == 'delete') {
        await DeleteUserAccessKey(token, key.UserAccessKeyId);
        await setSelectedKeys((prevState: any) =>
          prevState.filter((item: any) => item.ThirdPartyVendorId !== key.ThirdPartyVendor.ThirdPartyVendorId),
        );
        await setuserAccessKeyData((prevState: any) =>
          prevState.filter((item: any) => item.UserAccessKeyId !== key.UserAccessKeyId),
        );
        // addSnackbarMessage({
        //   message: 'Deleted User Access Key Successfully',
        //   type: 'success',
        // });
      } else {
        const reqBody = {
          userAccessKeyId: key.UserAccessKeyId,
        };
        await revokeUserAccessKey(token, reqBody);
        await setuserAccessKeyData((prevState: any) =>
          prevState.map((item: any) =>
            item.UserAccessKeyId == key.UserAccessKeyId
              ? { ...item, Status: UserAccessKeyStatus.Revoked }
              : { ...item },
          ),
        );
        await setSelectedKeys((prevState: any) =>
          prevState.filter((item: any) => item.ThirdPartyVendorId !== key.ThirdPartyVendor.ThirdPartyVendorId),
        );
      }
    };

    const columnDefs = React.useMemo<ColDef[]>(
      () => [
        {
          headerName: 'Status',
          field: 'Status',
          minWidth: 160,
          cellClass: classes.statusCell,
          filterValueGetter: (params) => getAccessKeyStatusLabel(params.data?.Status),
          cellRenderer: (params: ICellRendererParams<UserAccessKeyType>) => (
            <CellStatus
              status={getAccessKeyStatusLabel(params.data?.Status) as Status}
              colorsByStatus={accessKeyStatusColors}
            />
          ),
        },
        {
          headerName: 'Vendor Name',
          field: 'ThirdPartyVendor.VendorName',
          minWidth: 180,
          tooltipField: 'ThirdPartyVendor.VendorName',
        },
        {
          headerName: 'Access Key',
          field: 'UserAccessKey',
          minWidth: 320,
          flex: 2,
          cellRenderer: (params: ICellRendererParams<UserAccessKeyType>) => {
            const key = params.data;
            if (!key) return null;
            return (
              <AccessKeyCell
                accessKey={key}
                rowIndex={params.node.rowIndex ?? 0}
                classes={classes}
                onUnmask={unmaskKeyData}
                onCopy={copyUserAccessKey}
              />
            );
          },
        },
        {
          headerName: 'Last Updated',
          field: 'LastUpdatedDate',
          minWidth: 150,
          sort: 'desc',
          valueFormatter: (params) => (params.value ? moment(params.value).format('MM/DD/YYYY') : ''),
        },
        {
          headerName: 'Actions',
          minWidth: 120,
          maxWidth: 120,
          filter: false,
          floatingFilter: false,
          sortable: false,
          pinned: 'right',
          lockPinned: true,
          headerClass: 'left-align-header',
          cellClass: classes.actionsCell,
          cellRenderer: (params: ICellRendererParams<UserAccessKeyType>) => {
            const key = params.data;
            if (!key || key.Status === UserAccessKeyStatus.Revoked) return null;
            const isActivated = key.Status === UserAccessKeyStatus.Activated;

            return (
              <AgentNetButton
                variant="text"
                color="primary"
                size="small"
                className={classes.deleteRow}
                data-testid={`${isActivated ? 'RevokeUak' : 'DeleteUak'}${params.node.rowIndex ?? 0}`}
                onClick={() => handleAccessKeyDelete(key, isActivated ? 'revoke' : 'delete')}
              >
                {isActivated ? 'Revoke' : 'Delete'}
              </AgentNetButton>
            );
          },
        },
      ],
      [classes],
    );

    const defaultColDef = React.useMemo<ColDef>(
      () => ({
        flex: 1,
        cellClass: 'left-align-column',
        headerClass: 'left-align-header',
        filter: true,
        floatingFilter: false,
        sortable: true,
        resizable: true,
        suppressMenu: true,
        filterParams: { closeOnApply: true, suppressAndOrCondition: true },
      }),
      [],
    );

    return (
      <>
        {!userAccessKeyData?.length ? (
          <Grid item sm={12}>
            <div className={classes.validationMsg}>There are no user access keys.</div>
          </Grid>
        ) : null}
        <div>
          <Popper id={id} open={open} anchorEl={anchorEl} className={classes.popper}>
            <Paper elevation={8} className={classes.popperContent}>
              <div className={classes.popperHeader}>
                <Typography variant="h2" color="textPrimary">
                  Access Keys
                </Typography>
                <IconButton
                  className={classes.popperClose}
                  aria-label="Close User Access Keys Popper"
                  onClick={() => setAnchorEl(null)}
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>
              </div>
              <Autocomplete
                multiple
                id="checkboxes-tags-demo"
                options={accessKeyOptions}
                disableCloseOnSelect
                classes={{
                  option: classes.popperOption,
                  popperDisablePortal: classes.popperDisablePortal,
                  paper: classes.popperPaper,
                }}
                disableClearable
                disablePortal
                getOptionDisabled={(option: AccessKeyOptionType) =>
                  selectedKeys.some((selectedOption) => selectedOption.vendorName === option.vendorName)
                }
                getOptionLabel={(option: AccessKeyOptionType) => option.vendorName}
                PaperComponent={CustomPaper}
                PopperComponent={CustomPopper}
                onChange={(event, newValue: AccessKeyOptionType[]) => {
                  handleChange(newValue);
                }}
                renderOption={(option) => {
                  return (
                    <>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selectedKeys.filter((o) => o.vendorName === option.vendorName).length ? true : false}
                        className={classes.checkbox}
                        color="primary"
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        data-testid="AddUakCheckbox"
                      />
                      <Typography variant="body1" style={{ lineHeight: '190%' }}>
                        {option.vendorName}
                      </Typography>
                    </>
                  );
                }}
                renderInput={(params) => (
                  <div className={classes.textInputWrap}>
                    <AgentNetTextInput
                      {...params}
                      label={'Search Access Keys'}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      className={classes.textInput}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: null,
                        endAdornment: (
                          <IconButton
                            className={classes.deleteRow}
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            onClick={() => setMenuOpen(!menuOpen)}
                            data-testid="AddUakDropDown"
                          >
                            {menuOpen ? <ArrowDropUp color="disabled" /> : <ArrowDropDown color="disabled" />}
                          </IconButton>
                        ),
                      }}
                    />
                  </div>
                )}
                open={menuOpen}
                value={selectedKeys}
              />
            </Paper>
          </Popper>
        </div>
        {userAccessKeyData && userAccessKeyData.length > 0 ? (
          <div className={`ag-theme-alpine table-grid ${classes.table}`}>
            <DataTable
              rowData={userAccessKeyData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={{ domLayout: 'autoHeight' }}
            />
          </div>
        ) : null}
      </>
    );
  },
);

UserAccessKeyComponent.displayName = 'UserAccessKeyComponent';

export default UserAccessKeyComponent;
